import React, { useState } from 'react';
import { Box, Button, Typography, Chip, CircularProgress, Select, MenuItem, FormControl } from '@mui/material';
import { AspectRatio, Check } from '@mui/icons-material';
import { MiniPanelProps, ExpandedPanelProps } from '../../../../types/steps';
import MiniPanel from './MiniPanel';
import ExpandedPanel from './ExpandedPanel';
import { PaperSize } from '../../types';
import { PaperInfo } from '../../constants';
import { setSize, submitMap, useMapError, useSize } from '../../../../state/map';
import { useAppDispatch } from '../../../../state/types';
import { usePriceFormatted, usePriceIsLoading, useCurrency, setCurrency, usePrice } from '../../../../state/image';
import { Currency } from '../../../../types';

export const SizeMiniPanel: React.FC<MiniPanelProps> = ({ isActive, onExpand }) => {
  const currentSize = useSize();

  return (
    <MiniPanel isActive={isActive} onExpand={onExpand}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AspectRatio sx={{ color: 'primary.main' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {PaperInfo[currentSize].name}
          </Typography>
        </Box>
      </Box>
    </MiniPanel>
  );
};

export const SizeExpandedPanel: React.FC<ExpandedPanelProps> = ({ 
  isActive, 
  onMinimize, 
  onClose 
}) => {
  const currentSize = useSize();
  const dispatch = useAppDispatch();
  const [disable, setDisable] = useState(false);
  const error = useMapError();
  const price = usePrice();
  const priceFormatted = usePriceFormatted();
  const priceIsLoading = usePriceIsLoading();
  const currency = useCurrency();

  const handleSizeClick = (size: PaperSize) => {
    dispatch(setSize(size));
  };

  const sizeOptions = Object.values(PaperSize).map((size) => ({
    value: size,
    label: PaperInfo[size].name,
  }));

  return (
    <ExpandedPanel 
      isActive={isActive} 
      onMinimize={onMinimize} 
      onClose={onClose}
      title="Paper Size"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Paper Size Selection */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Choose Paper Size
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {sizeOptions.map(({ value, label }) => (
              <Box
                key={value}
                onClick={() => handleSizeClick(value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  borderRadius: '12px',
                  border: '2px solid',
                  borderColor: currentSize === value ? 'primary.main' : 'divider',
                  bgcolor: currentSize === value ? 'action.selected' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateX(4px)',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {label}
                  </Typography>
                  {value === PaperSize.A4 && (
                    <Chip 
                      label="Free" 
                      size="small" 
                      color="success"
                      sx={{ 
                        height: '20px', 
                        fontSize: '0.7rem', 
                        fontWeight: 600,
                        borderRadius: '4px'
                      }}
                    />
                  )}
                </Box>
                {currentSize === value && (
                  <Check sx={{ color: 'primary.main' }} />
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Price Display with Currency Selector */}
        <Box sx={{ 
          p: 2.5,
          bgcolor: 'background.paper',
          borderRadius: '12px',
          border: '2px solid',
          borderColor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-start' }}>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={currency}
                onChange={(e) => onCurrencyChange(e.target.value as Currency)}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '& .MuiSelect-select': {
                    py: 0.5,
                    fontSize: '0.875rem',
                    fontWeight: 600
                  }
                }}
              >
                {Object.values(Currency).map((curr) => (
                  <MenuItem key={curr} value={curr}>
                    {curr}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {priceIsLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Typography variant="h6" sx={{ fontWeight: 700 }} color="primary.main">
                {price === 0 ? "Free" : priceFormatted || 'Calculating...'}
              </Typography>
            )}
          </Box>
          <Button variant="contained" onClick={() => dispatch(submitMap())} disabled={!!error || disable}> Create </Button>
        </Box>
      </Box>
    </ExpandedPanel>
  );
};

